import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Medal, Award, Coins } from "lucide-react";
import { getLeaderboard, getOverview } from "@/lib/api";
import { formatDuration, formatDistance } from "@/lib/time";
import { usePlayer } from "@/context/PlayerContext";
import { flagFromCountry } from "@/lib/flags";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Leaderboard() {
  const { player } = usePlayer();
  const [rows, setRows] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getLeaderboard(25), getOverview()])
      .then(([lb, o]) => { setRows(lb); setOverview(o); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden" data-testid="leaderboard-page">
      {/* Backdrop */}
      <div className="fixed inset-0 opacity-20 pointer-events-none">
        <img src="https://images.unsplash.com/photo-1769120061986-a077f35b2569?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxzeW50aHdhdmUlMjBncmlkJTIwZGFya3xlbnwwfHx8fDE3ODY5NTI0OTJ8MA&ixlib=rb-4.1.0&q=85"
             alt="" className="w-full h-full object-cover" />
      </div>
      <div className="fixed inset-0 synth-grid opacity-30 pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-8 md:px-16 pt-16 pb-24">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-cyan-400 inline-flex items-center gap-2 transition-colors" data-testid="leaderboard-back-link">
            <ArrowLeft className="w-3 h-3" /> Back to garage
          </Link>
          {player && (
            <Link to="/play" className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="leaderboard-resume-link">
              Resume driving →
            </Link>
          )}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400">/// GLOBAL_TELEMETRY</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl text-white mt-2" data-testid="leaderboard-title">
            Top<span className="text-cyan-400 text-glow-cyan">.</span>Drivers
          </h1>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12" data-testid="leaderboard-overview">
          <StatCard label="Total Drivers" value={overview?.total_players ?? "—"} testid="stat-total-drivers" />
          <StatCard label="Total Time Driven" value={overview ? formatDuration(overview.total_time_seconds) : "—"} testid="stat-total-time" />
          <StatCard label="Distance Covered" value={overview ? formatDistance(overview.total_distance) : "—"} testid="stat-total-distance" />
        </div>

        {/* Table */}
        <div className="mt-12 border border-zinc-800 bg-black/60 backdrop-blur-xl">
          <Table data-testid="leaderboard-table">
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 w-16">Rank</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Driver</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Country</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Score</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Coins</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Time</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-right">Distance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center font-mono text-xs text-zinc-500 py-10">Fetching telemetry...</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center font-mono text-xs text-zinc-500 py-10" data-testid="leaderboard-empty">
                  No drivers yet. Be the first to hit the highway.
                </TableCell></TableRow>
              )}
              {rows.map((r, idx) => {
                const isMe = player && player.id === r.id;
                return (
                  <motion.tr
                    key={r.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03, duration: 0.3 }}
                    className={`border-zinc-800 transition-colors hover:bg-cyan-950/30 ${isMe ? "bg-cyan-500/5" : ""}`}
                    data-testid={`leaderboard-row-${idx}`}
                  >
                    <TableCell className="font-mono font-bold">
                      <RankBadge rank={idx + 1} />
                    </TableCell>
                    <TableCell className="font-mono text-white">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-sm ring-1 ring-white/10 inline-block"
                          style={{ background: r.car_color || "#06B6D4" }}
                          title="Car colour"
                          data-testid={`leaderboard-car-color-${idx}`}
                        />
                        <span className={isMe ? "text-cyan-400 text-glow-cyan" : ""}>{r.name}</span>
                        {isMe && <span className="ml-1 font-mono text-[9px] uppercase text-cyan-400 tracking-widest">you</span>}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-zinc-400">
                      <span className="inline-flex items-center gap-2" data-testid={`leaderboard-country-${idx}`}>
                        <span className="text-lg leading-none" aria-hidden="true">{flagFromCountry(r.country)}</span>
                        <span>{r.country}</span>
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-right text-yellow-400 text-glow-yellow tabular-nums">{r.high_score.toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-right text-yellow-300 tabular-nums">
                      <span className="inline-flex items-center gap-1 justify-end"><Coins className="w-3 h-3" /> {(r.coins_collected || 0).toLocaleString()}</span>
                    </TableCell>
                    <TableCell className="font-mono text-right text-white tabular-nums">{formatDuration(r.total_time_seconds)}</TableCell>
                    <TableCell className="font-mono text-right text-zinc-300 tabular-nums">{formatDistance(r.total_distance)}</TableCell>
                  </motion.tr>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-zinc-800 bg-black/60 backdrop-blur-xl p-6" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display font-black text-4xl text-cyan-400 text-glow-cyan mt-3 tabular-nums">{value}</div>
    </div>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return <span className="inline-flex items-center gap-2 text-yellow-400 text-glow-yellow"><Trophy className="w-4 h-4" /> 01</span>;
  if (rank === 2) return <span className="inline-flex items-center gap-2 text-zinc-300"><Medal className="w-4 h-4" /> 02</span>;
  if (rank === 3) return <span className="inline-flex items-center gap-2 text-amber-600"><Award className="w-4 h-4" /> 03</span>;
  return <span className="text-zinc-500">{String(rank).padStart(2, "0")}</span>;
}
